import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';

import { IUnitCardModel } from '../../../../../service/unit/interface/IUnitCardModel';
import { IUnitResultModel } from '../../../../../service/unitResult/IUnitResultModel';
import { Colors } from '../../../../design/color/Colors';
import { UnitCardHeader } from './components/UnitCardHeader';
import { UnitCardLangs } from './components/UnitCardLangs';
import { UnitCardStats } from './components/UnitCardStats';
import { UNIT_LIST_ITEMS_STYLES, getUnitStatus } from './utils/passingListUserCard';

export interface IUserUnitCardProperties {
  unit: IUnitCardModel;
  unitResult?: IUnitResultModel;
  action: any;
  isPassed: boolean | null;
  status: 'unimportant' | 'important';
}

const useStyles = makeStyles(() => ({
  flexCenter: { display: 'flex', alignItems: 'center' },
  innerPadding: { padding: '24px 24px 0 24px', minHeight: '168px', paddingBottom: '32px' },
  desc: { opacity: 0.7, marginBottom: '32px' },
  imgContainer: { flex: '0 0 30%', height: '185px', position: 'relative' },
  content: { display: 'flex', flexDirection: 'column', flex: '0 0 70%' },
  imgCover: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    position: 'absolute' as const,
    top: 0,
    left: 0,
  },
}));

export const UserUnitCard = observer(({ action, unitResult, isPassed, status, unit }: IUserUnitCardProperties) => {
  const classes = useStyles();
  const unitStatus = getUnitStatus(isPassed);
  const dataCyUniqueValue = `data-cy-${unit.settings.titleHeading}`;

  const handleClick = useCallback(() => {
    action(unit.id);
  }, [action, unit.id]);

  const styles = UNIT_LIST_ITEMS_STYLES[unitStatus];

  return (
    <Box
      data-cy={dataCyUniqueValue}
      sx={{
        background: styles.baseColor,
        borderColor: styles.borderColor,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '16px',
        clipPath: 'inset(0 round 16px)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': { background: styles.hoverColor },
      }}
    >
      <Box id={unit.id} onClick={handleClick} className={classes.flexCenter} sx={{ flex: 1 }}>
        <Box className={classes.content}>
          <Box className={classes.innerPadding}>
            <UnitCardHeader unit={unit} status={status} />
            <Box className={classes.desc}>{unit.settings.description}</Box>
            <UnitCardStats unitResult={unitResult} unit={unit} />
            {unit?.practice?.isEnabled && <UnitCardLangs unit={unit} />}
          </Box>
        </Box>
        <Box className={classes.imgContainer}>
          <img
            alt=""
            src={unit.settings.preview?.data || '/pictures/unit-cover-default.png'}
            className={classes.imgCover}
            style={!unit.settings.preview?.data ? { filter: Colors.logoFilter } : undefined}
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: '4px',
          backgroundColor: styles.progressColor,
          width: `${((unitResult?.totalUnitResult || 0) * 100) / unit.score}%`,
        }}
      />
    </Box>
  );
});
